import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { EVENT_TYPE_LABELS } from "../../shared/event-types";
import { eventCardHref } from "../../shared/inquiry";
import { STAGE_LABELS, type SalesStage, hasPermission } from "../../shared/permissions";
import { api } from "../api";
import { useSession } from "../App";
import { Banner, messageOf, onSubmit } from "./ui";

type BookingChip = { guests: number; title: string | null; event_id: string | null };
type InquiryChip = { id: string; title: string; stage: string; event_type: string; adults: number | null; date: string; note: string | null };
type Cell = { date: string; state: string; detail: string; guests: number; bookings: BookingChip[]; inquiries?: InquiryChip[] };

const EVENT_TYPES = EVENT_TYPE_LABELS;

function stageLabel(stage: string): string {
  return stage in STAGE_LABELS ? STAGE_LABELS[stage as SalesStage] : stage.replace(/_/g, " ");
}
type Row = { id: string; name: string; ownership: string; days: Cell[] };

const STATUS: Record<string, string> = {
  confirmed: "Κράτηση",
  tentative: "Προκράτηση",
  available: "Ανοιχτό",
  blocked: "Κλειστό",
  unknown: "Κενό",
};
const LEGEND = [
  ["confirmed", "Κράτηση · 2026"],
  ["y2027", "Κράτηση · 2027"],
  ["blocked", "Κλειστό"],
  ["available", "Ανοιχτό"],
  ["tentative", "Προκράτηση"],
  ["unknown", "Κενό"],
] as const;

function toneClass(day: Cell): string {
  return day.state === "confirmed" && day.date.startsWith("2027-") ? "confirmed y2027" : day.state;
}

function statusLabel(day: Cell): string {
  if (day.state === "confirmed" && day.date.startsWith("2027-")) return "Κράτηση 2027";
  return STATUS[day.state] ?? day.state;
}
const WEEKDAYS = ["ΔΕ", "ΤΡ", "ΤΕ", "ΠΕ", "ΠΑ", "ΣΑ", "ΚΥ"];

function mondayIndex(iso: string): number {
  const [year, month, day] = iso.split("-").map(Number);
  return (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7;
}

function dayNumber(iso: string): string {
  return String(Number(iso.slice(8, 10)));
}

function longDate(iso: string): string {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("el-GR", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
}
type Hold = { id: string; venue_id: string; space_id: string; occupancy_start: string; occupancy_end: string; guests: number; status: string; expires_at: string | null; reason: string | null };
type Space = { id: string; name: string; venue_id: string };

function shift(days: number): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function AvailabilityPage() {
  const { user } = useSession();
  const [from, setFrom] = useState(shift(0));
  const [to, setTo] = useState(shift(13));
  const [matrix, setMatrix] = useState<Row[]>([]);
  const [holds, setHolds] = useState<Hold[]>([]);
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState<{ venueId: string; date: string } | null>(null);
  const canWrite = hasPermission(user.role, user.permissions, "availability.write");
  const canHold = hasPermission(user.role, user.permissions, "holds.write");
  const canConfirm = hasPermission(user.role, user.permissions, "bookings.confirm");
  const canReconcile = hasPermission(user.role, user.permissions, "holds.reconcile");

  function load() {
    setLoading(true);
    setError(null);
    Promise.all([
      api<{ matrix: Row[]; holds: Hold[] }>(`/api/availability?from=${from}&to=${to}`),
      api<{ venues: { id: string; spaces?: Space[] }[] }>("/api/venues"),
    ]).then(([board, venues]) => {
      setMatrix(board.matrix);
      setHolds(board.holds);
      setSpaces(venues.venues.flatMap((venue) => (venue.spaces ?? []).map((space) => ({ ...space, venue_id: venue.id }))));
    }).catch((reason: unknown) => setError(messageOf(reason)))
      .finally(() => setLoading(false));
  }

  useEffect(load, [from, to]);

  async function act(path: string, body: Record<string, unknown>) {
    setError(null);
    try {
      await api(path, { method: "POST", body: JSON.stringify(body) });
      setNote("Η διαθεσιμότητα ενημερώθηκε.");
      load();
    } catch (reason) {
      setError(messageOf(reason));
    }
  }

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Διαθεσιμότητα</h1>
          <p className="lede">Πάτα μια μέρα για να δεις ποιος είναι στον χώρο. Οι ώρες είναι Europe/Athens. Μια προκράτηση που έληξε δεν πιάνει πια τον χώρο.</p>
        </div>
      </header>
      <Banner error={error} note={note} />
      <div className="range-bar">
        <label className="range-field">Από<input type="date" value={from} onChange={(event) => setFrom(event.target.value)} /></label>
        <label className="range-field">Έως<input type="date" value={to} onChange={(event) => setTo(event.target.value)} /></label>
      </div>
      <div className="legend" aria-hidden="true">
        {LEGEND.map(([state, label]) => <span key={state}><i className={`swatch ${state}`} />{label}</span>)}
      </div>
      {loading ? <p>Φόρτωση ημερολογίου…</p> : null}
      {!loading && error ? <p>Το ημερολόγιο δεν φορτώθηκε.</p> : null}
      {!loading && !error && matrix.length === 0 ? <p>Δεν υπάρχουν ενεργοί χώροι στο ημερολόγιο.</p> : null}
      <div className="venue-cal-list">
        {matrix.map((row) => {
          const lead = row.days[0] ? mondayIndex(row.days[0].date) : 0;
          const booked = row.days.filter((day) => day.state === "confirmed").length;
          const selected = picked?.venueId === row.id ? row.days.find((day) => day.date === picked.date) : undefined;
          return (
            <section className="venue-cal" key={row.id}>
              <header>
                <h2>{row.name}</h2>
                <p className="meta">{booked} κρατήσεις σε αυτό το διάστημα · {row.ownership === "partner" ? "Συνεργάτης" : "Εσωτερικός"}</p>
              </header>
              <div className="cal-grid cal-weekdays">{WEEKDAYS.map((name) => <span key={name}>{name}</span>)}</div>
              <div className="cal-grid">
                {Array.from({ length: lead }, (_, index) => <span key={`pad-${index}`} />)}
                {row.days.map((day) => (
                  <button
                    key={day.date}
                    type="button"
                    className={`cal-day ${toneClass(day)}${selected?.date === day.date ? " selected" : ""}`}
                    aria-pressed={selected?.date === day.date}
                    aria-label={`${row.name} ${longDate(day.date)} ${statusLabel(day)}${day.guests ? `, ${day.guests} άτομα` : ""}`}
                    onClick={() => setPicked({ venueId: row.id, date: day.date })}
                  >
                    <span>{dayNumber(day.date)}</span>
                    {day.guests > 0 ? <small>{day.guests}</small> : null}
                  </button>
                ))}
              </div>
              {selected ? (
                <div className="cal-detail">
                  <strong>{longDate(selected.date)}</strong>
                  <span className={`pill ${toneClass(selected)}`}>{statusLabel(selected)}</span>
                  <p>{selected.detail}</p>
                  {(selected.inquiries ?? []).length === 0 ? (
                    selected.bookings.length === 0 ? null : <p className="meta">{selected.guests > 0 ? `${selected.guests} άτομα στο ημερολόγιο. Δεν υπάρχει αίτημα για αυτή την κράτηση.` : "Δεν υπάρχει αίτημα για αυτή την κράτηση."}</p>
                  ) : (
                    <ul>
                      {(selected.inquiries ?? []).map((inquiry) => (
                        <li key={inquiry.id}>
                          <Link className="inquiry-link" to={eventCardHref(inquiry.id)}>
                            <span className="meta">Ονοματεπώνυμο</span>
                            <strong>{inquiry.title}</strong>
                            <span className="meta">{stageLabel(inquiry.stage)} · {EVENT_TYPES[inquiry.event_type] ?? inquiry.event_type} · {inquiry.adults ?? "άγνωστα"} άτομα{inquiry.date !== selected.date ? ` · ${longDate(inquiry.date)}` : ""}</span>
                            {inquiry.note ? <span>{inquiry.note}</span> : null}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : <p className="meta">Πάτα μια μέρα.</p>}
            </section>
          );
        })}
      </div>
      <div className="split" style={{ marginTop: 16 }}>
        {canWrite ? (
          <form className="panel" onSubmit={onSubmit(async (body) => act("/api/availability/windows", { venue_id: body.venue_id, local_date: body.local_date, state: "blocked", notes: body.notes || "Staff blackout", whole_day: true }))}>
            <h2>Κλείσιμο ημέρας</h2>
            <label>Χώρος
              <select name="venue_id">{matrix.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}</select>
            </label>
            <label>Ημερομηνία<input name="local_date" type="date" required defaultValue={from} /></label>
            <label>Σημείωση<input name="notes" placeholder="Επισκευές" /></label>
            <button type="submit">Αποθήκευση κλεισίματος</button>
          </form>
        ) : null}
        {canHold ? (
          <form className="panel" onSubmit={onSubmit(async (body) => act("/api/holds", { space_id: body.space_id, date: body.date, start_time: body.start_time || "18:00", end_time: body.end_time || "23:00", guests: Number(body.guests), expires_at: new Date(body.expires_at).toISOString(), reason: body.reason || "Staff hold", idempotency_key: `hold-${body.space_id}-${body.date}-${Date.now()}` }))}>
            <h2>Προκράτηση</h2>
            <label>Χώρος
              <select name="space_id">{spaces.map((space) => <option key={space.id} value={space.id}>{space.name}</option>)}</select>
            </label>
            <div className="fields">
              <label>Ημερομηνία<input name="date" type="date" required /></label>
              <label>Ατομα<input name="guests" defaultValue="20" /></label>
              <label>Λήξη<input name="expires_at" type="datetime-local" required /></label>
            </div>
            <label>Λόγος<input name="reason" /></label>
            <button type="submit">Κράτησε τον χώρο</button>
            <p className="meta">Η δημιουργία ή η αποστολή πρότασης δεν κρατά τον χώρο. Δύο αποκλειστικές επιβεβαιώσεις για το ίδιο διάστημα δεν μπορούν να περάσουν και οι δύο.</p>
          </form>
        ) : null}
      </div>
      <section className="panel" style={{ marginTop: 16 }}>
        <div className="actions">
          <h2>Ενεργές προκρατήσεις</h2>
          {canReconcile ? <button className="secondary" type="button" onClick={() => void act("/api/admin/reconcile", {})}>Σήμανση ληγμένων</button> : null}
        </div>
        {holds.length === 0 ? <p>Δεν υπάρχουν ενεργές προκρατήσεις.</p> : null}
        <table>
          <thead><tr><th>Χώρος</th><th>Κατάληψη</th><th>Ατομα</th><th>Λήξη</th><th></th></tr></thead>
          <tbody>
            {holds.map((hold) => (
              <tr key={hold.id}>
                <td>{spaces.find((space) => space.id === hold.space_id)?.name ?? hold.space_id}<div className="meta">{hold.reason}</div></td>
                <td>{hold.occupancy_start.slice(0, 16)} → {hold.occupancy_end.slice(11, 16)}</td>
                <td>{hold.guests}</td>
                <td>{hold.expires_at ? hold.expires_at.slice(0, 16) : "Χωρίς λήξη"}</td>
                <td className="actions">
                  {canHold ? <button className="secondary" type="button" onClick={() => void act(`/api/holds/${hold.id}/release`, { idempotency_key: `release-${hold.id}-${Date.now()}` })}>Αποδέσμευση</button> : null}
                  {canConfirm ? <button type="button" onClick={() => void act(`/api/holds/${hold.id}/confirm`, { idempotency_key: `confirm-${hold.id}` })}>Επιβεβαίωση</button> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
}
